import { useCallback, useEffect, useState } from 'react';
import type { Layout } from 'react-grid-layout';
import { DASHBOARD_CARDS, getDefaultLayout } from './dashboardCardsConfig';

const LAYOUT_STORAGE_KEY = 'dashboard_layout_v1';
const HIDDEN_CARDS_STORAGE_KEY = 'dashboard_hidden_cards_v1';
const KNOWN_CARDS_STORAGE_KEY = 'dashboard_known_cards_v1';

/** Registry dışı ek kart (kullanıcının kurduğu dinamik rapor kartları) */
export interface ExtraCardDef {
  id: string;
  defaultLayout: Omit<Layout, 'i'>;
}

/** Registry + ek kartların varsayılan layout'u; ek kartlar mevcut kartların altına yerleşir */
export const buildDefaultLayoutWithExtras = (extraCards: ExtraCardDef[]): Layout[] => {
  const defaults = getDefaultLayout();
  let nextY = defaults.reduce((max, l) => Math.max(max, l.y + l.h), 0);
  for (const card of extraCards) {
    defaults.push({ i: card.id, ...card.defaultLayout, x: 0, y: nextY });
    nextY += card.defaultLayout.h;
  }
  return defaults;
};

/**
 * Kayıtlı layout'u varsayılanlarla birleştirir:
 * - Kayıtta olan kartlar kayıtlı konum/boyutlarıyla gelir
 * - Registry'ye sonradan eklenen kartlar varsayılan konumlarıyla eklenir (ileriye dönük uyumluluk)
 * - Registry'den (veya özel rapor deposundan) kaldırılmış kartların kayıtları atılır
 */
const mergeWithDefaults = (saved: Layout[] | null, extraCards: ExtraCardDef[]): Layout[] => {
  const defaults = buildDefaultLayoutWithExtras(extraCards);
  if (!saved || !Array.isArray(saved) || saved.length === 0) return defaults;

  const savedById = new Map(saved.map((l) => [l.i, l]));
  return defaults.map((def) => {
    const s = savedById.get(def.i);
    if (!s) return def;
    return {
      ...def,
      x: s.x,
      y: s.y,
      w: s.w,
      h: s.h,
    };
  });
};

const loadLayout = (extraCards: ExtraCardDef[]): Layout[] => {
  try {
    const raw = localStorage.getItem(LAYOUT_STORAGE_KEY);
    return mergeWithDefaults(raw ? JSON.parse(raw) : null, extraCards);
  } catch {
    return buildDefaultLayoutWithExtras(extraCards);
  }
};

const loadHiddenCards = (extraCardIds: string[]): string[] => {
  try {
    const raw = localStorage.getItem(HIDDEN_CARDS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    const validIds = new Set([...DASHBOARD_CARDS.map((c) => c.id), ...extraCardIds]);
    // Registry'de (ve özel raporlarda) olmayan id'leri temizle
    let hidden = Array.isArray(parsed)
      ? parsed.filter((id: unknown): id is string => typeof id === 'string' && validIds.has(id))
      : [];

    // ✅ defaultHidden kartlar: bu cihazda daha önce görülmemiş (known listesinde olmayan)
    // defaultHidden kartlar otomatik gizlenir. Kullanıcı modalden açarsa normal toggle
    // akışı geçerli olur; kart "known" işaretlendiği için tekrar otomatik gizlenmez.
    const knownRaw = localStorage.getItem(KNOWN_CARDS_STORAGE_KEY);
    const knownParsed = knownRaw ? JSON.parse(knownRaw) : [];
    const known = new Set<string>(
      Array.isArray(knownParsed) ? knownParsed.filter((id: unknown): id is string => typeof id === 'string') : []
    );

    let knownChanged = false;
    for (const card of DASHBOARD_CARDS) {
      if (!known.has(card.id)) {
        known.add(card.id);
        knownChanged = true;
        if (card.defaultHidden && !hidden.includes(card.id)) {
          hidden = [...hidden, card.id];
        }
      }
    }

    if (knownChanged) {
      try {
        localStorage.setItem(KNOWN_CARDS_STORAGE_KEY, JSON.stringify([...known]));
        localStorage.setItem(HIDDEN_CARDS_STORAGE_KEY, JSON.stringify(hidden));
      } catch {
        /* noop */
      }
    }

    return hidden;
  } catch {
    return DASHBOARD_CARDS.filter((c) => c.defaultHidden).map((c) => c.id);
  }
};

/**
 * Dashboard kart düzeni ve görünürlüğü (localStorage kalıcılığı ile).
 * Sidebar ayarlarıyla aynı desen: cihaz bazlı localStorage.
 * extraCards: kullanıcının kurduğu dinamik rapor kartları (custom_ önekli id'ler);
 * eklenince/silinince layout ve gizli kart listesi yeniden birleştirilir.
 */
export const useDashboardLayout = (extraCards: ExtraCardDef[] = []) => {
  const [layout, setLayout] = useState<Layout[]>(() => loadLayout(extraCards));
  const [hiddenCards, setHiddenCards] = useState<string[]>(() =>
    loadHiddenCards(extraCards.map((c) => c.id))
  );

  const extraKey = extraCards.map((c) => c.id).join(',');
  useEffect(() => {
    setLayout(loadLayout(extraCards));
    setHiddenCards(loadHiddenCards(extraCards.map((c) => c.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraKey]);

  /** Yeni layout'u kaydeder. Sadece verilen kartlar güncellenir, geri kalanlar korunur (gizli kartların konumu kaybolmaz). */
  const saveLayout = useCallback((newLayout: Layout[]) => {
    setLayout((prev) => {
      const newById = new Map(newLayout.map((l) => [l.i, l]));
      const merged = prev.map((item) => {
        const updated = newById.get(item.i);
        return updated ? { ...item, x: updated.x, y: updated.y, w: updated.w, h: updated.h } : item;
      });
      try {
        localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(merged));
      } catch {
        /* storage dolu olabilir - sessiz geç */
      }
      return merged;
    });
  }, []);

  /** Düzeni varsayılana döndürür (özel rapor kartları da varsayılan konuma iner) */
  const resetLayout = useCallback(() => {
    const defaults = buildDefaultLayoutWithExtras(extraCards);
    setLayout(defaults);
    try {
      localStorage.removeItem(LAYOUT_STORAGE_KEY);
    } catch {
      /* noop */
    }
    return defaults;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraKey]);

  /** Kart görünürlüğünü değiştirir ve anında kaydeder */
  const toggleCardVisibility = useCallback((cardId: string) => {
    setHiddenCards((prev) => {
      const next = prev.includes(cardId) ? prev.filter((id) => id !== cardId) : [...prev, cardId];
      try {
        localStorage.setItem(HIDDEN_CARDS_STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* noop */
      }
      return next;
    });
  }, []);

  return { layout, hiddenCards, saveLayout, resetLayout, toggleCardVisibility };
};
